<template>
  <div
    class="text-center column items-center"
    data-test-id="site-selector-with-zones"
  >
    <section-heading
      v-if="showHeading"
      :heading-color="headingColor"
      :heading-color-hex="headingColorHex"
      :heading-font-size="headingFontSize"
      :heading-align="headingAlign ? headingAlign : 'center'"
      :heading-type="headingType"
      :heading-background-image-url="headingBackgroundImageUrl"
      :heading-background-image-repeat="headingBackgroundImageRepeat"
      :title="!title ? $t('siteLocation.selectLocationHeading') : title"
      :is-mobile-view="isMobileView"
      class="container q-mx-auto col-auto q-pa-lg"
    />
    <div
      v-if="initialDisplayType === SELECTOR_DISPLAY_TYPES.SELECT_BOX"
      class="container q-mx-auto q-pa-sm"
    >
      <site-selector-zones-selectbox
        :redirect-home-page="redirectHomePage"
        :compact-view="compactView"
        :outline="buttonType === BUTTON_TYPES.OUTLINE"
        :class="{
          [getClasses(buttonType, buttonColor, buttonColorHex)]: true,
          'w-700 q-mx-auto': isWidget
        }"
        :style="getStyles(buttonType, buttonColor, buttonColorHex, buttonBorderRadius)"
        :push="buttonType === BUTTON_TYPES.STANDARD"
        size="lg"
      />
    </div>
    <div
      v-else
      class="container q-mx-auto col"
    >
      <site-location-look-up
        :sites-without-location-zone-id="sitesWithoutLocationZoneId"
        :location-zones="locationZones"
        :site-locations="siteLocations"
        :all-sites-have-images="allSitesHaveImages"
        :show-site-image="showSiteImage"
        :show-site-description="showSiteDescription"
        :button-type="buttonType"
        :button-color="buttonColor"
        :button-color-hex="buttonColorHex"
        :button-border-radius="buttonBorderRadius"
        :field-type="fieldType"
        :field-outlined="fieldOutlined"
        :compact-view="compactView"
        :card-color="cardColor"
        :card-color-hex="cardColorHex"
        :card-border-radius="cardBorderRadius"
        :redirect-home-page="redirectHomePage"
        :is-widget="isWidget"
        :dense="dense"
        @all-locations-shown="shown => showAllLocation = shown"
        @loading="value => loading = value"
      />
      <q-inner-loading
        style="z-index: 1;"
        :showing="loading"
      />

      <template v-if="showLocationListAndCurrentLocationButton && showAllLocation">
        <site-selector-locations-list
          :sites-without-location-zone-id="sitesWithoutLocationZoneId"
          :location-zones="locationZones"
          :site-locations="siteLocations"
          :all-sites-have-images="allSitesHaveImages"
          :show-site-image="showSiteImage"
          :show-site-description="showSiteDescription"
          :default-opened="defaultExpanded"
          :card-color="cardColor"
          :card-color-hex="cardColorHex"
          :card-border-radius="cardBorderRadius"
          :redirect-home-page="redirectHomePage"
          :is-widget="isWidget"
          :class="{
            'q-mx-sm': isWidget && showSiteImage,
            'w-1000 q-mx-auto': !showSiteImage
          }"
          @select="$emit('select', $event)"
        >
          <template #title>
            <span
              class="text-h5"
            >
              {{ $t('siteLocation.all') }}
            </span>
          </template>
        </site-selector-locations-list>
      </template>
    </div>
  </div>
</template>

<script>
import { BUTTON_TYPES, SELECTOR_DISPLAY_TYPES } from 'src/models/page/Section'
import SiteLocationsListMixin from 'src/modules/consumer/components/mixins/SiteLocationsListMixin'
import SectionHeading from 'src/modules/consumer/components/SectionHeading'
import SiteLocationLookUp from 'src/modules/consumer/components/site-selector/SiteLocationLookUp'
import SiteSelectorLocationsList from 'src/modules/consumer/components/site-selector/SiteSelectorLocationsList'
import SiteSelectorZonesSelectbox from 'src/modules/consumer/components/site-selector/SiteSelectorZonesSelectbox'
export default {
  name: 'SiteSelectorWithZones',
  components: {
    SiteSelectorLocationsList,
    SiteSelectorZonesSelectbox,
    SiteLocationLookUp,
    SectionHeading
  },
  mixins: [SiteLocationsListMixin],
  props: {
    showHeading: {
      type: Boolean,
      default: true,
      required: false
    },
    headingColor: {
      type: String,
      default: 'responsive',
      required: false
    },
    headingColorHex: {
      type: String,
      default: '#FFFFFF',
      required: false
    },
    headingAlign: {
      type: String,
      default: 'center',
      required: false
    },
    headingFontSize: {
      type: String,
      default: 'h4',
      required: false
    },
    headingType: {
      type: String,
      default: 'default',
      required: false
    },
    headingBackgroundImageUrl: {
      type: String,
      default: null,
      required: false
    },
    headingBackgroundImageRepeat: {
      type: Boolean,
      required: false,
      default: true
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    cardColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    cardBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    cardColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    initialDisplayType: {
      type: String,
      required: false,
      default: SELECTOR_DISPLAY_TYPES.DEFAULT
    },
    compactView: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.OUTLINE
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    buttonBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    fieldType: {
      type: String,
      required: false,
      default: 'filled'
    },
    fieldOutlined: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteDescription: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultExpanded: {
      type: Boolean,
      required: false,
      default: false
    },
    redirectHomePage: {
      type: Boolean,
      required: false,
      default: false
    },
    isWidget: {
      type: Boolean,
      required: false,
      default: false
    },
    dense: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['select'],
  data () {
    return {
      loading: false,
      showAllLocation: true
    }
  },
  computed: {
    showLocationListAndCurrentLocationButton () {
      return this.initialDisplayType === SELECTOR_DISPLAY_TYPES.DEFAULT
    }
  },
  created () {
    _.extend(this, {
      SELECTOR_DISPLAY_TYPES,
      BUTTON_TYPES
    })
  }
}
</script>

<style lang="scss" scoped>
.w-700 {
  width: 100%;
  max-width: 700px;
}
.w-1000 {
  width: 100%;
  max-width: 1000px;
}
</style>
